/**
 * Delay node configuration type and validation
 */

export type DelayConfigType = {
  delayType: 'fixed' | 'until';
  duration?: number;
  durationUnit?: 'seconds' | 'minutes' | 'hours' | 'days';
  untilDateTime?: string;
  untilExpression?: string;
};

export const defaultDelayConfig: DelayConfigType = {
  delayType: 'fixed',
  duration: 5,
  durationUnit: 'minutes',
};

export const validateDelayConfig = (
  config: DelayConfigType,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (config.delayType === 'fixed') {
    if (!config.duration || config.duration <= 0) {
      errors.duration = 'Duration must be greater than 0';
    }
    if (!config.durationUnit) {
      errors.durationUnit = 'Duration unit is required';
    }
  } else if (config.delayType === 'until') {
    if (!config.untilDateTime && !config.untilExpression) {
      errors.untilDateTime = 'Either datetime or expression is required';
    }
  }

  return errors;
};

/**
 * Convert duration + unit to seconds for backend
 */
export const computeDurationSeconds = (
  duration: number,
  unit: string,
): number => {
  switch (unit) {
    case 'seconds':
      return duration;
    case 'minutes':
      return duration * 60;
    case 'hours':
      return duration * 3600;
    case 'days':
      return duration * 86400;
    default:
      return duration;
  }
};

/**
 * Get preview text for the node display
 */
export const getDelayPreviewText = (config: DelayConfigType): string => {
  if (!config.delayType) return 'Configure delay';

  if (config.delayType === 'fixed') {
    return `Wait ${config.duration || 0} ${config.durationUnit || 'seconds'}`;
  }

  if (config.untilDateTime) {
    return `Until ${config.untilDateTime}`;
  }

  if (config.untilExpression) {
    return `Until expression`;
  }

  return 'Configure delay';
};
